import type { ReactNode } from "react";

import { FeaturePageBackground } from "@/features/feature-page/feature-page-background";
import { cn } from "@/lib/utils";
import type { ResolvedFeaturePageVisual } from "@garimpa/api/featurePages/types";
import type { FeaturePageBlock } from "@garimpa/lib/featurePage";

const widthClasses = {
  prose: "max-w-3xl",
  content: "max-w-6xl",
  wide: "max-w-[1440px]",
  fullBleed: "max-w-none",
} as const;
const spacingClasses = {
  compact: "py-10 md:py-14",
  normal: "py-16 md:py-24",
  immersive: "py-24 md:py-36",
} as const;
const surfaceClasses = {
  plain: "",
  tinted: "bg-secondary/35",
  card: "bg-card",
  image: "bg-background",
} as const;

export function FeaturePageSection({
  block,
  resolvedBackground,
  children,
  className,
  innerClassName,
  spacingClassName,
  priorityBackground = false,
  labelledById,
}: {
  block: FeaturePageBlock;
  resolvedBackground?: ResolvedFeaturePageVisual | null;
  children: ReactNode;
  className?: string;
  innerClassName?: string;
  spacingClassName?: string;
  priorityBackground?: boolean;
  labelledById?: string;
}) {
  const headingId = labelledById ?? (block.heading ? `${block.id}-heading` : undefined);
  const Root = headingId ? "section" : "div";
  return (
    <Root
      id={block.id}
      aria-labelledby={headingId}
      className={cn(
        "relative isolate scroll-mt-36 overflow-hidden border-b border-border/40",
        spacingClassName ?? spacingClasses[block.spacing],
        surfaceClasses[block.surface],
        className,
      )}
    >
      {block.background && resolvedBackground ? (
        <FeaturePageBackground
          spec={block.background}
          visual={resolvedBackground}
          priority={priorityBackground}
        />
      ) : null}
      <div
        className={cn(
          "relative z-10 mx-auto w-full px-4 md:px-6",
          widthClasses[block.width],
          innerClassName,
        )}
      >
        {children}
      </div>
    </Root>
  );
}
